import { trpc } from "@/lib/trpc";
import { useParams } from "wouter";

// ─── Status pipeline definition ─────────────────────────────────────────────

type SvrStepKey =
  | "scheduled"
  | "in_progress"
  | "report_sent"
  | "closed";

interface SvrStep {
  key: SvrStepKey;
  label: string;
  description: string;
}

const SVR_STEPS: SvrStep[] = [
  {
    key: "scheduled",
    label: "Visit Scheduled",
    description: "Your service visit has been scheduled. Our technician will arrive at the agreed time.",
  },
  {
    key: "in_progress",
    label: "Technician On-Site",
    description: "Our technician is currently on-site attending to your service request.",
  },
  {
    key: "report_sent",
    label: "Report Sent",
    description: "The service report has been prepared and sent to you.",
  },
  {
    key: "closed",
    label: "Visit Closed",
    description: "The service visit has been completed and closed. Thank you for choosing Clarity.",
  },
];

const STATUS_TO_STEP: Record<string, SvrStepKey> = {
  scheduled: "scheduled",
  in_progress: "in_progress",
  pending_review: "in_progress",
  report_sent: "report_sent",
  closed: "closed",
};

const ARRIVAL_WINDOW_LABELS: Record<string, string> = {
  "9am-10am": "9:00am – 10:00am",
  "10am-11am": "10:00am – 11:00am",
  "11am-12pm": "11:00am – 12:00pm",
  "2pm-3pm": "2:00pm – 3:00pm",
  "3pm-4pm": "3:00pm – 4:00pm",
  "4pm-5pm": "4:00pm – 5:00pm",
};

function formatDate(d: Date | string | null | undefined): string {
  if (!d) return "";
  return new Date(d).toLocaleDateString("en-SG", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function getStepIndex(steps: SvrStep[], key: SvrStepKey): number {
  return steps.findIndex((s) => s.key === key);
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function TrackSiteVisit() {
  const params = useParams<{ token: string }>();
  const token = params.token;

  const { data: visit, isLoading, error } = trpc.siteVisits.getByToken.useQuery(
    { token: token ?? "" },
    { enabled: !!token, retry: false }
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-sm text-gray-500">Loading your service report…</p>
        </div>
      </div>
    );
  }

  if (error || !visit) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <SvrHeader />
        <div className="flex-1 flex items-center justify-center px-6">
          <div className="text-center max-w-sm">
            <div className="text-5xl mb-4">🔍</div>
            <h2 className="text-xl font-semibold mb-2">Report Not Found</h2>
            <p className="text-sm text-gray-500">
              We could not find a service visit matching this link. Please check the link in your
              notification message or contact us directly.
            </p>
            <div className="mt-6">
              <a
                href="https://wa.me/6565116511"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-green-600 text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-green-700 transition-colors"
              >
                <WhatsAppIcon />
                WhatsApp Us
              </a>
            </div>
          </div>
        </div>
        <SvrFooter />
      </div>
    );
  }

  const currentStepKey = STATUS_TO_STEP[visit.status] ?? "scheduled";
  const currentIdx = getStepIndex(SVR_STEPS, currentStepKey);

  const address = [
    visit.customerAddress ?? "",
    visit.customerPostalCode ? ` Singapore ${visit.customerPostalCode}` : "",
  ].join("").trim() || "your premises";

  const picContactDigits = visit.picContact?.replace(/[^0-9]/g, "") ?? "";
  const picWhatsAppUrl = picContactDigits
    ? `https://wa.me/${picContactDigits.length === 8 ? "65" + picContactDigits : picContactDigits}`
    : null;

  const hasReport = visit.customerReport && visit.customerReport.trim() !== "";

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <SvrHeader />

      <main className="flex-1 px-4 py-8 max-w-2xl mx-auto w-full">

        {/* Visit reference card */}
        <div className="border border-gray-200 rounded-lg p-5 mb-8">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-widest mb-1">Service Visit</p>
              <p className="text-2xl font-bold tracking-wider text-black">{visit.visitNumber}</p>
              <p className="mt-1 text-sm font-medium text-gray-800">
                {visit.customerName?.trim() || "—"}
              </p>
            </div>
            <SvrStatusPill status={visit.status} />
          </div>

          <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Visit Date</p>
              <p className="font-medium text-gray-800">{formatDate(visit.visitDate)}</p>
            </div>
            {visit.arrivalWindow && (
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Arrival Window</p>
                <p className="font-medium text-gray-800">
                  {ARRIVAL_WINDOW_LABELS[visit.arrivalWindow] ?? visit.arrivalWindow}
                </p>
              </div>
            )}
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Location</p>
              <p className="font-medium text-gray-800">{address}</p>
            </div>
            {visit.technicianName && (
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Technician</p>
                <p className="font-medium text-gray-800">{visit.technicianName}</p>
              </div>
            )}
          </div>
        </div>

        {/* Progress stepper */}
        <div className="mb-8">
          <h3 className="text-xs text-gray-500 uppercase tracking-widest mb-4">Visit Progress</h3>
          <VerticalStepper steps={SVR_STEPS} currentIdx={currentIdx} />
        </div>

        {/* Complaint description */}
        {visit.complaintDescription && (
          <div className="mb-8 border border-gray-200 rounded-lg p-5">
            <p className="text-xs text-gray-500 uppercase tracking-widest mb-2">Issue Reported</p>
            <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">
              {visit.complaintDescription}
            </p>
          </div>
        )}

        {/* Service report */}
        {hasReport && (
          <div className="mb-8 border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-5 py-3 border-b border-gray-200">
              <p className="text-xs text-gray-500 uppercase tracking-widest font-semibold">
                Service Report
              </p>
            </div>
            <div className="p-5">
              <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">
                {visit.customerReport}
              </p>
              {visit.reportSentAt && (
                <p className="text-xs text-gray-400 mt-4">
                  Report sent on {formatDate(visit.reportSentAt)}
                  {visit.reportSentVia ? ` via ${visit.reportSentVia}` : ""}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Contact PIC */}
        <div className="border border-gray-200 rounded-lg p-5">
          <p className="text-xs text-gray-500 uppercase tracking-widest mb-3">Need Assistance?</p>
          {visit.picName && (
            <p className="text-sm text-gray-700 mb-3">
              Your account manager is <strong>{visit.picName}</strong>.
            </p>
          )}
          <div className="flex flex-wrap gap-3">
            {picWhatsAppUrl && (
              <a
                href={picWhatsAppUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-green-600 text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-green-700 transition-colors"
              >
                <WhatsAppIcon />
                {visit.picName ? `WhatsApp ${visit.picName.split(" ")[0]}` : "WhatsApp Us"}
              </a>
            )}
            {!picWhatsAppUrl && (
              <a
                href="https://wa.me/6565116511"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-green-600 text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-green-700 transition-colors"
              >
                <WhatsAppIcon />
                WhatsApp Us
              </a>
            )}
          </div>
        </div>
      </main>

      <SvrFooter />
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function WhatsAppIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current" xmlns="http://www.w3.org/2000/svg">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

function SvrHeader() {
  return (
    <header className="border-b border-gray-100 px-6 py-4 flex items-center justify-between">
      <span className="text-xs text-gray-400 uppercase tracking-widest">Service Visit Report</span>
      <img
        src="https://d2xsxph8kpxj0f.cloudfront.net/310519663414100320/brqZLwPGMDsYTB8zGXzCpe/clarity-logo_db6df1c5.jpg"
        alt="Clarity"
        className="h-7 object-contain"
      />
    </header>
  );
}

function SvrFooter() {
  return (
    <footer className="border-t border-gray-100 px-6 py-5 text-center">
      <p className="text-xs text-gray-400">
        © Clarity Group Pte Ltd · Please quote your service visit reference in all correspondence.
      </p>
    </footer>
  );
}

function SvrStatusPill({ status }: { status: string }) {
  const styles: Record<string, string> = {
    scheduled: "bg-blue-50 text-blue-700 border-blue-200",
    in_progress: "bg-amber-50 text-amber-700 border-amber-200",
    pending_review: "bg-yellow-50 text-yellow-700 border-yellow-200",
    report_sent: "bg-green-50 text-green-700 border-green-200",
    closed: "bg-gray-50 text-gray-600 border-gray-200",
  };
  const labels: Record<string, string> = {
    scheduled: "Scheduled",
    in_progress: "On-Site",
    pending_review: "Report Pending",
    report_sent: "Report Sent",
    closed: "Closed",
  };
  const style = styles[status] || "bg-gray-50 text-gray-600 border-gray-200";
  const label = labels[status] || status;
  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border whitespace-nowrap ${style}`}>
      {label}
    </span>
  );
}

function VerticalStepper({
  steps,
  currentIdx,
}: {
  steps: SvrStep[];
  currentIdx: number;
}) {
  return (
    <div className="space-y-0">
      {steps.map((step, idx) => {
        const done = idx < currentIdx;
        const current = idx === currentIdx;
        const last = idx === steps.length - 1;
        return (
          <div key={step.key} className="flex gap-4">
            <div className="flex flex-col items-center">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 shrink-0 ${
                  done
                    ? "bg-black border-black text-white"
                    : current
                    ? "bg-white border-black text-black ring-4 ring-black/10"
                    : "bg-white border-gray-300 text-gray-400"
                }`}
              >
                {done ? "✓" : idx + 1}
              </div>
              {!last && (
                <div
                  className={`w-0.5 flex-1 mt-1 mb-1 min-h-[24px] ${
                    done ? "bg-black" : "bg-gray-200"
                  }`}
                />
              )}
            </div>
            <div className="pb-5">
              <p
                className={`text-sm font-medium leading-tight ${
                  current ? "text-black" : done ? "text-gray-600" : "text-gray-400"
                }`}
              >
                {step.label}
              </p>
              {current && (
                <p className="text-xs text-gray-500 mt-0.5">{step.description}</p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
